'use client';

import { useState, useRef, useEffect } from 'react';
import {
  useReadingMode,
  saveForOffline,
  FONT_SIZE_MAP,
  LINE_HEIGHT_MAP,
  THEME_MAP,
  type ReadingTheme,
  type FontFamily,
  type FontSize,
} from '@/lib/hooks/useReadingMode';

interface ReadingModeProps {
  title: string;
  contentHtml: string;
  url: string;
}

export default function ReadingMode({ title, contentHtml, url }: ReadingModeProps) {
  const {
    isActive,
    prefs,
    toggle,
    exit,
    setTheme,
    setFontFamily,
    setFontSize,
    setLineHeight,
  } = useReadingMode();

  const [showControls, setShowControls] = useState(false);
  const [savedOffline, setSavedOffline] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scrollPercent, setScrollPercent] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);

  // Track scroll progress within reading mode
  useEffect(() => {
    if (!isActive || !contentRef.current) return;
    const el = contentRef.current;
    const handleScroll = () => {
      const scrollTop = el.scrollTop;
      const scrollHeight = el.scrollHeight - el.clientHeight;
      if (scrollHeight > 0) {
        setScrollPercent(Math.round((scrollTop / scrollHeight) * 100));
      }
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [isActive]);

  const handleSaveOffline = async () => {
    setSaving(true);
    const success = await saveForOffline(url);
    setSavedOffline(success);
    setSaving(false);
  };

  const theme = THEME_MAP[prefs.theme];

  if (!isActive) {
    return (
      <button
        onClick={toggle}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-light-border dark:border-border text-light-text-secondary dark:text-foreground-secondary hover:border-accent/40 hover:text-accent transition-colors"
        title="Enter reading mode"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
        Reading Mode
      </button>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col transition-colors duration-300"
      style={{ backgroundColor: theme.bg, color: theme.text }}
    >
      {/* Progress bar */}
      <div className="h-0.5 w-full" style={{ backgroundColor: `${theme.accent}20` }}>
        <div
          className="h-full transition-all duration-150"
          style={{ width: `${scrollPercent}%`, backgroundColor: theme.accent }}
        />
      </div>

      {/* Top bar */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b shrink-0"
        style={{ borderColor: `${theme.accent}20` }}
      >
        <button
          onClick={exit}
          className="flex items-center gap-2 text-sm opacity-70 hover:opacity-100 transition-opacity"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Exit
        </button>

        <span className="text-sm opacity-50 hidden sm:block truncate max-w-md">{title}</span>

        <div className="flex items-center gap-2">
          {/* Save offline */}
          <button
            onClick={handleSaveOffline}
            disabled={saving || savedOffline}
            className="text-sm opacity-70 hover:opacity-100 transition-opacity disabled:opacity-40"
            title={savedOffline ? 'Saved for offline' : 'Save for offline reading'}
          >
            {saving ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : savedOffline ? (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            )}
          </button>

          {/* Settings toggle */}
          <button
            onClick={() => setShowControls(!showControls)}
            className="text-sm opacity-70 hover:opacity-100 transition-opacity"
            title="Reading settings"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
          </button>
        </div>
      </div>

      {/* Settings panel */}
      {showControls && (
        <div
          className="px-4 py-3 border-b shrink-0"
          style={{ borderColor: `${theme.accent}20` }}
        >
          <div className="max-w-2xl mx-auto flex flex-wrap gap-6 text-sm">
            {/* Theme */}
            <div className="flex items-center gap-2">
              <span className="opacity-60">Theme:</span>
              {(['dark', 'light', 'sepia'] as ReadingTheme[]).map(t => (
                <button
                  key={t}
                  onClick={() => setTheme(t)}
                  className={`px-2 py-1 rounded capitalize ${prefs.theme === t ? 'font-bold' : 'opacity-60 hover:opacity-100'}`}
                  style={prefs.theme === t ? { backgroundColor: `${theme.accent}30` } : undefined}
                >
                  {t}
                </button>
              ))}
            </div>

            {/* Font */}
            <div className="flex items-center gap-2">
              <span className="opacity-60">Font:</span>
              {(['sans', 'serif'] as FontFamily[]).map(f => (
                <button
                  key={f}
                  onClick={() => setFontFamily(f)}
                  className={`px-2 py-1 rounded capitalize ${prefs.fontFamily === f ? 'font-bold' : 'opacity-60 hover:opacity-100'}`}
                  style={{
                    fontFamily: f === 'serif' ? 'Georgia, serif' : 'var(--font-sans), sans-serif',
                    ...(prefs.fontFamily === f ? { backgroundColor: `${theme.accent}30` } : {}),
                  }}
                >
                  {f}
                </button>
              ))}
            </div>

            {/* Size */}
            <div className="flex items-center gap-2">
              <span className="opacity-60">Size:</span>
              {(['small', 'medium', 'large', 'xlarge'] as FontSize[]).map(s => (
                <button
                  key={s}
                  onClick={() => setFontSize(s)}
                  className={`px-2 py-1 rounded ${prefs.fontSize === s ? 'font-bold' : 'opacity-60 hover:opacity-100'}`}
                  style={prefs.fontSize === s ? { backgroundColor: `${theme.accent}30` } : undefined}
                >
                  {s === 'xlarge' ? 'XL' : s.charAt(0).toUpperCase()}
                </button>
              ))}
            </div>

            {/* Spacing */}
            <div className="flex items-center gap-2">
              <span className="opacity-60">Spacing:</span>
              {(['compact', 'normal', 'relaxed'] as const).map(l => (
                <button
                  key={l}
                  onClick={() => setLineHeight(l)}
                  className={`px-2 py-1 rounded capitalize ${prefs.lineHeight === l ? 'font-bold' : 'opacity-60 hover:opacity-100'}`}
                  style={prefs.lineHeight === l ? { backgroundColor: `${theme.accent}30` } : undefined}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content area */}
      <div
        ref={contentRef}
        className="flex-1 overflow-y-auto"
        style={{ scrollBehavior: 'smooth' }}
      >
        <div
          className="max-w-2xl mx-auto px-6 py-12"
          style={{
            fontSize: FONT_SIZE_MAP[prefs.fontSize],
            lineHeight: LINE_HEIGHT_MAP[prefs.lineHeight],
            fontFamily: prefs.fontFamily === 'serif' ? 'Georgia, Cambria, "Times New Roman", serif' : 'var(--font-sans), system-ui, sans-serif',
          }}
        >
          <h1
            className="font-bold mb-8"
            style={{
              fontSize: `calc(${FONT_SIZE_MAP[prefs.fontSize]} * 1.8)`,
              lineHeight: '1.2',
              color: theme.text,
            }}
          >
            {title}
          </h1>

          <div
            className="reading-mode-content"
            dangerouslySetInnerHTML={{ __html: contentHtml }}
            style={{ color: theme.text }}
          />
        </div>
      </div>

      {/* Reading mode content styles */}
      <style>{`
        .reading-mode-content h1,
        .reading-mode-content h2,
        .reading-mode-content h3,
        .reading-mode-content h4 {
          font-weight: 700;
          margin-top: 2em;
          margin-bottom: 0.75em;
          line-height: 1.3;
          color: ${theme.text};
        }
        .reading-mode-content h2 { font-size: 1.4em; }
        .reading-mode-content h3 { font-size: 1.2em; }
        .reading-mode-content p {
          margin-bottom: 1.2em;
        }
        .reading-mode-content a {
          color: ${theme.accent};
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .reading-mode-content a:hover {
          opacity: 0.8;
        }
        .reading-mode-content ul,
        .reading-mode-content ol {
          margin-bottom: 1.2em;
          padding-left: 1.5em;
        }
        .reading-mode-content li {
          margin-bottom: 0.4em;
        }
        .reading-mode-content blockquote {
          border-left: 3px solid ${theme.accent};
          padding-left: 1em;
          margin: 1.5em 0;
          opacity: 0.85;
          font-style: italic;
        }
        .reading-mode-content pre {
          background: ${prefs.theme === 'dark' ? '#0d2438' : prefs.theme === 'sepia' ? '#e8dcc4' : '#f0f0f0'};
          padding: 1em;
          border-radius: 8px;
          overflow-x: auto;
          margin: 1.5em 0;
          font-family: var(--font-mono), 'JetBrains Mono', monospace;
          font-size: 0.875em;
          line-height: 1.6;
        }
        .reading-mode-content code {
          font-family: var(--font-mono), 'JetBrains Mono', monospace;
          font-size: 0.9em;
        }
        .reading-mode-content p > code {
          background: ${prefs.theme === 'dark' ? '#0d2438' : prefs.theme === 'sepia' ? '#e8dcc4' : '#f0f0f0'};
          padding: 0.15em 0.4em;
          border-radius: 4px;
        }
        .reading-mode-content img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 1.5em 0;
        }
        .reading-mode-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1.5em 0;
        }
        .reading-mode-content th,
        .reading-mode-content td {
          border: 1px solid ${theme.accent}40;
          padding: 0.5em 0.75em;
          text-align: left;
        }
        .reading-mode-content th {
          font-weight: 600;
          background: ${theme.accent}15;
        }
        .reading-mode-content hr {
          border: none;
          border-top: 1px solid ${theme.accent}30;
          margin: 2em 0;
        }
      `}</style>
    </div>
  );
}
